import { PDFDocument } from 'pdf-lib';
import { pngAspectRatio } from '../utils/pngSize';

export type SignaturePlacement = {
  /** 0-based page index (PDF). For DOCX treated as page-relative A4 placement. */
  pageIndex: number;
  /** Fractions of page width/height (top-left origin in UI) */
  leftFrac: number;
  topFrac: number;
  widthFrac: number;
  heightFrac: number;
};

function assertPreviewSafe(bytes: Uint8Array): void {
  // C2PA / Content Credentials survive a naive pdf-lib rewrite and break macOS Preview.
  const head = new TextDecoder('latin1').decode(
    bytes.subarray(0, Math.min(bytes.byteLength, 512_000)),
  );
  if (
    /Content Credentials/i.test(head) ||
    /\/Type\s*\/EmbeddedFile/i.test(head) ||
    /\bc2pa\b/i.test(head)
  ) {
    throw new Error(
      'PDF enthält Content Credentials, die macOS Vorschau nicht öffnen kann. Bitte das Signatur-Fenster komplett schließen und neu öffnen, dann erneut signieren.',
    );
  }
}

/**
 * Rebuild pages into a brand-new PDF (flatten via embedPdf), then stamp the PNG.
 * Avoids re-serializing Associated Files / C2PA that break Preview.
 */
export async function embedSignatureOnPdf(
  pdfBytes: Uint8Array,
  pngBytes: Uint8Array,
  placement: SignaturePlacement,
): Promise<Uint8Array> {
  const src = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
  const pageCount = src.getPageCount();
  if (placement.pageIndex < 0 || placement.pageIndex >= pageCount) {
    throw new Error('Ungültige PDF-Seite.');
  }

  const doc = await PDFDocument.create();
  doc.setProducer('Signatur App (flat)');
  doc.setCreator('Signatur App');

  // Flatten: draw each source page as an embedded page XObject (no catalog /AF baggage).
  const embedded = await doc.embedPdf(src, src.getPageIndices());
  for (const ep of embedded) {
    const page = doc.addPage([ep.width, ep.height]);
    page.drawPage(ep);
  }

  const page = doc.getPage(placement.pageIndex);
  const pageWidth = page.getWidth();
  const pageHeight = page.getHeight();
  const image = await doc.embedPng(pngBytes);
  const aspect = pngAspectRatio(pngBytes, image.width / Math.max(image.height, 1));

  const width = Math.max(8, placement.widthFrac * pageWidth);
  const height = Math.max(8, width / aspect);
  const x = placement.leftFrac * pageWidth;
  const y = pageHeight - placement.topFrac * pageHeight - height;

  page.drawImage(image, { x, y, width, height });

  const saved = await doc.save({ useObjectStreams: false });
  const out = saved instanceof Uint8Array ? saved : new Uint8Array(saved);
  assertPreviewSafe(out);
  return out;
}

export { signedFileName } from './signNames';
